import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import Input from '../components/ui/Input';
import DateSelect from '../components/ui/DateSelect';
import PhoneInput from '../components/ui/PhoneInput';
import CuiInput from '../components/ui/CuiInput';
import {
  isValidEmailLive,
  isValidCui,
  isValidCnp,
  isValidName,
  cnpBirthDateISO,
  getCnpBirthDate,
} from '../utils/validation';
import { isValidPhoneNumber } from 'libphonenumber-js';

const CompanyForm = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [createdInfo, setCreatedInfo] = useState(null);
  const [error, setError] = useState('');
  // Autocompletare date firmă de la ANAF (după CUI).
  const [anafLoading, setAnafLoading] = useState(false);
  const [anafNote, setAnafNote] = useState(null); // { type: 'success'|'warning'|'error', msg }
  // Validare LIVE per-câmp: erori afișate inline pe măsură ce completezi (după ce ai atins câmpul),
  // ca să nu fie nevoie să apeși Salvează și să te întorci. Chei plate: „company.cui", „admin.email",
  // „extra.0.cnp" etc. Banner-ul de la submit rămâne ca verificare finală.
  const [liveErrors, setLiveErrors] = useState({});
  const [touched, setTouched] = useState({});
  const markTouched = (key) => setTouched((t) => (t[key] ? t : { ...t, [key]: true }));

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  // SUPER_ADMIN can choose; SSM_COMPANY_ADMIN always creates CLIENT
  const [companyType, setCompanyType] = useState(isSuperAdmin ? 'SSM' : 'CLIENT');
  const [ssmCompanies, setSsmCompanies] = useState([]);
  const [parentCompanyId, setParentCompanyId] = useState('');

  useEffect(() => {
    if (isSuperAdmin) {
      api
        .get('/companies?type=SSM')
        .then((r) => setSsmCompanies(r.data || []))
        .catch(() => {});
    }
  }, [isSuperAdmin]);

  const [companyData, setCompanyData] = useState({
    name: '',
    cui: '',
    address: '',
    phone: '',
    email: '',
  });

  const [adminData, setAdminData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    homeAddress: '',
    birthDate: '',
    birthPlace: '',
    badgeNumber: '',
    bloodGroup: '',
    qualification: '',
    specialAuthorizations: '',
    cnp: '',
    hireDate: '',
  });
  const [adminIsSSMInstructor, setAdminIsSSMInstructor] = useState(false);
  const [adminIsCadruTehnicPSI, setAdminIsCadruTehnicPSI] = useState(false);
  const [adminHasEPI, setAdminHasEPI] = useState(false);
  const [adminCanManageEmployees, setAdminCanManageEmployees] = useState(false);
  const [adminIsCLM, setAdminIsCLM] = useState(false);

  // Servicii oferite de firmă — folosite pentru filtrarea aplicației.
  // Default true pentru ambele (cazul cel mai comun).
  const [offersSSM, setOffersSSM] = useState(true);
  const [offersSU, setOffersSU] = useState(true);
  // Raport ITM — add-on plătit separat. Default false (opt-in): nu toate firmele îl vor.
  const [offersITM, setOffersITM] = useState(false);
  // #21: ghid de pagină (tururi contextuale pe pagini) — activ implicit; firma poate fi creată fără el.
  const [pageGuideEnabled, setPageGuideEnabled] = useState(true);
  // Wizardul mare de onboarding (prezentare platformă) — OPT-IN per firmă. Default false:
  // SSM-istul decide explicit pentru clienții care vor onboarding ghidat la prima logare.
  const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
  // Email-uri automate (bun venit, notificări) — orice rol care poate crea o firmă (SUPER_ADMIN
  // sau SSM) decide de la creare. Poate fi schimbat oricând ulterior din tab-ul Info. Default false.
  const [emailsEnabled, setEmailsEnabled] = useState(false);
  // Firmă demo (SUPER_ADMIN only) — pentru conturi de prezentare la clienți potențiali. CUI/CNP/
  // email pot fi fictive; sare peste verificările „lume reală" (checksum, MX) la creare și ulterior
  // pentru oricine e adăugat sub această firmă. Default false.
  const [isDemo, setIsDemo] = useState(false);

  // Decide dacă adminul firmei poate fi marcat ca instructor intern SSM/PSI.
  // - Firma SSM: da (admin = SSM_COMPANY_ADMIN, posibil specialist)
  // - Firma CLIENT standalone (fără SSM extern): da (își face SSM-ul singur)
  // - Firma CLIENT cu SSM extern: nu (de SSM se ocupă firma SSM, nu adminul clientului)
  //
  // ATENȚIE: când SSM_COMPANY_ADMIN creează un client, parent-ul se setează automat în backend
  // din firma utilizatorului — `parentCompanyId` rămâne gol în formular. Deci pentru rolurile SSM
  // non-super-admin, firma client va AVEA mereu un SSM extern (a lor) → flag-uri NICIODATĂ.
  const adminCanBeInternalSSM =
    companyType === 'SSM' || (companyType === 'CLIENT' && !parentCompanyId && isSuperAdmin);

  // Administratori adiționali — primul admin e obligatoriu (mai sus); restul sunt opționali și au
  // EXACT aceleași câmpuri ca primul admin (datele de identitate sunt necesare pentru fișe, traseu etc).
  // Backend-ul îi creează cu rolul de admin prefixat și parolă temporară comună.
  const emptyExtraAdmin = () => ({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthDate: '',
    birthPlace: '',
    homeAddress: '',
    badgeNumber: '',
    bloodGroup: '',
    qualification: '',
    specialAuthorizations: '',
    cnp: '',
    hireDate: '',
    isSSMInstructor: false,
    isCadruTehnicPSI: false,
    hasEPI: false,
    canManageEmployees: false,
    isCLM: false,
  });
  const [extraUsers, setExtraUsers] = useState([]);
  const addExtraAdmin = () => setExtraUsers((prev) => [...prev, emptyExtraAdmin()]);
  const removeExtraAdmin = (idx) => setExtraUsers((prev) => prev.filter((_, i) => i !== idx));
  const updateExtraAdmin = (idx, field, value) =>
    setExtraUsers((prev) => prev.map((u, i) => (i === idx ? { ...u, [field]: value } : u)));

  // Bifa CLM la creare firmă: modelul multi-CLM permite ORICÂȚI conducători (toți devin CLM permanenți
  // „pe toată firma", fiindcă la creare nu există încă departamente). Pentru temporari/departamente
  // → view-ul „Conducători (CLM)" după creare.
  const tryToggleAdminCLM = (newValue) => setAdminIsCLM(newValue);
  const tryToggleExtraCLM = (idx, newValue) => updateExtraAdmin(idx, 'isCLM', newValue);

  const handleCompanyChange = (e) => {
    setCompanyData({ ...companyData, [e.target.name]: e.target.value });
    setError(''); // Clear error cand user editeaza
  };

  const handleAdminChange = (e) => {
    setAdminData({ ...adminData, [e.target.name]: e.target.value });
    setError('');
  };

  // CNP admin: la schimbare, dacă e valid și data nașterii e goală, o pre-completăm din CNP.
  const handleAdminCnpChange = (e) => {
    const value = e.target.value;
    setAdminData((prev) => {
      const next = { ...prev, cnp: value };
      if (!prev.birthDate && isValidCnp(value)) {
        const iso = cnpBirthDateISO(value);
        if (iso) next.birthDate = iso;
      }
      return next;
    });
    setError('');
  };

  // CNP admin adițional: la fel, pre-completează data nașterii a rândului dacă e goală.
  const updateExtraAdminCnp = (idx, value) => {
    setExtraUsers((prev) =>
      prev.map((u, i) => {
        if (i !== idx) return u;
        const next = { ...u, cnp: value };
        if (!u.birthDate && isValidCnp(value)) {
          const iso = cnpBirthDateISO(value);
          if (iso) next.birthDate = iso;
        }
        return next;
      })
    );
  };

  // Validare CUI la step 1
  const validateStep1 = async () => {
    if (!companyData.name || !companyData.cui) {
      setError(t('companyForm.fillNameAndCui'));
      return false;
    }
    // Validare format CUI (cifra de control) — sărită pentru firme demo (CUI poate fi fictiv).
    if (!isDemo && !isValidCui(companyData.cui)) {
      setError(t('companyForm.cuiInvalid'));
      return false;
    }
    // Email firmă — opțional, dar dacă e completat trebuie să fie format valid.
    if (companyData.email && !isValidEmailLive(companyData.email)) {
      setError(t('companyForm.companyEmailInvalid'));
      return false;
    }
    if (!offersSSM && !offersSU) {
      setError(t('companyForm.selectAtLeastOneService'));
      return false;
    }

    // Verifică CUI duplicat — la demo sărim verificarea de format (checksum), păstrăm doar
    // verificarea de unicitate (submit-ul o re-validează oricum server-side).
    try {
      const response = await api.post('/companies/check-cui', {
        cui: companyData.cui,
      });

      if (!isDemo && response.data.invalidFormat) {
        setError(t('companyForm.cuiInvalid'));
        return false;
      }
      if (response.data.exists) {
        setError(t('companyForm.cuiExists', { cui: companyData.cui }));
        return false;
      }

      return true;
    } catch (err) {
      setError(t('companyForm.cuiCheckError'));
      return false;
    }
  };

  // Validare format telefon prin libphonenumber (RO ca țară implicită pentru numere naționale).
  // Returnează true dacă numărul e un număr de telefon real, false altfel.
  const isPhoneValid = (rawPhone) => {
    if (!rawPhone) return false;
    try {
      return isValidPhoneNumber(String(rawPhone).trim(), 'RO');
    } catch (_) {
      return false;
    }
  };

  // Validări logice de dată (naștere/angajare). Întoarce un mesaj de eroare sau null.
  const validateDates = (birthDate, hireDate) => {
    const today = new Date();
    let bd = null;
    if (birthDate) {
      bd = new Date(birthDate);
      if (isNaN(bd.getTime())) return t('companyForm.birthDateInvalid');
      if (bd > today) return t('companyForm.birthDateFuture');
      const age = (today - bd) / (365.25 * 24 * 3600 * 1000);
      if (age < 16) return t('companyForm.ageMin16');
      if (age > 100) return t('companyForm.birthDateTooOld');
    }
    if (hireDate) {
      const hd = new Date(hireDate);
      if (isNaN(hd.getTime())) return t('companyForm.hireDateInvalid');
      if (bd && hd < bd) return t('companyForm.hireBeforeBirth');
    }
    return null;
  };

  // Potrivire CNP ↔ data nașterii. True dacă nu e nimic de comparat.
  const cnpMatchesBirth = (cnp, birthDate) => {
    const cd = getCnpBirthDate(cnp);
    if (!cd || !birthDate) return true;
    const bd = new Date(birthDate);
    if (isNaN(bd.getTime())) return true;
    return (
      cd.getUTCFullYear() === bd.getUTCFullYear() &&
      cd.getUTCMonth() === bd.getUTCMonth() &&
      cd.getUTCDate() === bd.getUTCDate()
    );
  };

  // Calculează erorile per-câmp (chei plate). Validează doar dacă există conținut (câmpurile
  // obligatorii goale rămân pe seama banner-ului de la submit, ca să nu „urle" pe câmpuri neatinse).
  const computeFieldErrors = () => {
    const fe = {};
    if (!isDemo && companyData.cui && !isValidCui(companyData.cui))
      fe['company.cui'] = t('companyForm.fieldCuiChecksum');
    if (companyData.email && !isValidEmailLive(companyData.email))
      fe['company.email'] = t('companyForm.fieldCompanyEmail');
    if (companyData.phone && !isPhoneValid(companyData.phone))
      fe['company.phone'] = t('companyForm.fieldPhoneInvalid');

    const personErrors = (p, prefix) => {
      if (p.firstName && !isValidName(p.firstName))
        fe[`${prefix}.firstName`] = t('companyForm.fieldNameChars');
      if (p.lastName && !isValidName(p.lastName))
        fe[`${prefix}.lastName`] = t('companyForm.fieldNameChars');
      if (p.email && !isValidEmailLive(p.email))
        fe[`${prefix}.email`] = t('companyForm.fieldEmailInvalid');
      if (p.phone && !isPhoneValid(p.phone))
        fe[`${prefix}.phone`] = t('companyForm.fieldPhoneInvalid');
      const dateErr = validateDates(p.birthDate, p.hireDate);
      if (dateErr) fe[`${prefix}.birthDate`] = dateErr;
      if (p.cnp && !isDemo) {
        if (!isValidCnp(p.cnp)) fe[`${prefix}.cnp`] = t('companyForm.fieldCnpChecksum');
        else if (!cnpMatchesBirth(p.cnp, p.birthDate))
          fe[`${prefix}.cnp`] = t('companyForm.fieldCnpMismatch');
      }
    };
    personErrors(adminData, 'admin');
    extraUsers.forEach((u, i) => personErrors(u, `extra.${i}`));
    return fe;
  };

  // Recalcul live; afișăm doar erorile câmpurilor atinse.
  useEffect(() => {
    const fe = computeFieldErrors();
    setLiveErrors(() => {
      const shown = {};
      for (const k in fe) if (touched[k]) shown[k] = fe[k];
      return shown;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyData, adminData, extraUsers, touched, isDemo]);

  // Preia automat datele firmei de la ANAF după CUI și completează nume/adresă/telefon + prefix TVA.
  const handleAnafLookup = async () => {
    if (!isValidCui(companyData.cui)) {
      setAnafNote({
        type: 'error',
        msg: t('companyForm.anafEnterValidCui'),
      });
      return;
    }
    setAnafLoading(true);
    setAnafNote(null);
    setError('');
    try {
      const { data } = await api.post('/companies/anaf-lookup', { cui: companyData.cui });
      const digits = String(companyData.cui).toUpperCase().replace(/^RO/, '').replace(/\D/g, '');
      setCompanyData((prev) => ({
        ...prev,
        name: data.name || prev.name,
        address: data.address || prev.address,
        phone: data.phone || prev.phone,
        // Prefixul RO/— se aliniază cu statutul de plătitor TVA raportat de ANAF.
        cui: (data.vatPayer ? 'RO' : '') + digits,
      }));
      if (data.radiata) {
        setAnafNote({ type: 'error', msg: t('companyForm.anafRadiata', { name: data.name }) });
      } else if (data.inactive) {
        setAnafNote({ type: 'warning', msg: t('companyForm.anafInactive', { name: data.name }) });
      } else {
        setAnafNote({
          type: 'success',
          msg:
            t('companyForm.anafDataFetched', { name: data.name }) +
            (data.phone ? '' : t('companyForm.anafPhoneNotPublic')),
        });
      }
    } catch (err) {
      setAnafNote({
        type: 'error',
        msg: err.response?.data?.error || t('companyForm.anafFetchError'),
      });
    } finally {
      setAnafLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validare câmpuri obligatorii admin — hireDate OBLIGATORIE; telefonul e OPȚIONAL.
      // Domiciliul se completează la declararea traseului (nu aici).
      const required = ['email', 'firstName', 'lastName', 'birthDate', 'birthPlace', 'hireDate'];
      const missing = required.filter((f) => !adminData[f] || !String(adminData[f]).trim());
      if (missing.length > 0) {
        const labels = {
          email: t('common.email'),
          firstName: t('employees.firstName'),
          lastName: t('employees.lastName'),
          birthDate: t('employees.birthDate'),
          birthPlace: t('employees.birthPlace'),
          phone: t('common.phone'),
          hireDate: t('employees.hireDate'),
        };
        setError(
          t('companyForm.completeFields', { fields: missing.map((f) => labels[f]).join(', ') })
        );
        setLoading(false);
        return;
      }
      // Email admin — format valid (obligatoriu).
      if (!isValidEmailLive(adminData.email)) {
        setError(t('companyForm.adminEmailInvalid'));
        setLoading(false);
        return;
      }
      // Nume/prenume admin — doar litere, spații, cratimă, apostrof.
      if (!isValidName(adminData.firstName) || !isValidName(adminData.lastName)) {
        setError(t('companyForm.adminNameChars'));
        setLoading(false);
        return;
      }
      // Date logice admin (naștere/angajare).
      const adminDateErr = validateDates(adminData.birthDate, adminData.hireDate);
      if (adminDateErr) {
        setError(t('companyForm.adminDateError', { error: adminDateErr }));
        setLoading(false);
        return;
      }
      // Telefon OPȚIONAL — dar dacă e completat, validăm formatul. Vezi `isPhoneValid`.
      if (adminData.phone && !isPhoneValid(adminData.phone)) {
        setError(t('companyForm.adminPhoneFormat'));
        setLoading(false);
        return;
      }
      // CNP — opțional; dacă e completat: cifra de control + potrivire cu data nașterii.
      // Sărit la demo — CNP poate fi fictiv (testare semnătură fără date reale).
      if (adminData.cnp && !isDemo) {
        if (!isValidCnp(adminData.cnp)) {
          setError(t('companyForm.adminCnpInvalid'));
          setLoading(false);
          return;
        }
        if (!cnpMatchesBirth(adminData.cnp, adminData.birthDate)) {
          setError(t('companyForm.adminCnpMismatch'));
          setLoading(false);
          return;
        }
      }

      // CLM la creare: modelul multi-CLM permite oricâți conducători (toți devin CLM permanenți
      // „pe toată firma"). Fără restricție de număr aici.

      // Validare administratori adiționali: aceleași câmpuri obligatorii ca primul admin
      // (firstName, lastName, email, birthDate, birthPlace). CNP-ul (dacă completat) format 13 cifre.
      const adminRole = companyType === 'SSM' ? 'SSM_COMPANY_ADMIN' : 'COMPANY_ADMIN';
      const cleanExtraAdmins = [];
      for (let i = 0; i < extraUsers.length; i++) {
        const u = extraUsers[i];
        const allEmpty =
          !u.email?.trim() &&
          !u.firstName?.trim() &&
          !u.lastName?.trim() &&
          !u.birthDate &&
          !u.birthPlace?.trim();
        if (allEmpty) continue; // rând complet gol → ignoră
        // Data angajării obligatorie și pentru admin-ii adiționali; telefonul OPȚIONAL.
        const reqLabels = {
          firstName: t('employees.firstName'),
          lastName: t('employees.lastName'),
          email: t('common.email'),
          birthDate: t('employees.birthDate'),
          birthPlace: t('employees.birthPlace'),
          hireDate: t('employees.hireDate'),
        };
        const missingFields = Object.keys(reqLabels).filter((f) => !u[f] || !String(u[f]).trim());
        if (missingFields.length > 0) {
          setError(
            t('companyForm.extraAdminComplete', {
              num: i + 1,
              fields: missingFields.map((f) => reqLabels[f]).join(', '),
            })
          );
          setLoading(false);
          return;
        }
        if (!isValidEmailLive(u.email)) {
          setError(t('companyForm.extraAdminEmailInvalid', { num: i + 1 }));
          setLoading(false);
          return;
        }
        if (!isValidName(u.firstName) || !isValidName(u.lastName)) {
          setError(t('companyForm.extraAdminNameChars', { num: i + 1 }));
          setLoading(false);
          return;
        }
        const extraDateErr = validateDates(u.birthDate, u.hireDate);
        if (extraDateErr) {
          setError(t('companyForm.extraAdminDateError', { num: i + 1, error: extraDateErr }));
          setLoading(false);
          return;
        }
        if (u.phone && !isPhoneValid(u.phone)) {
          setError(t('companyForm.extraAdminPhoneFormat', { num: i + 1 }));
          setLoading(false);
          return;
        }
        if (u.cnp && !isDemo) {
          if (!isValidCnp(u.cnp)) {
            setError(t('companyForm.extraAdminCnpInvalid', { num: i + 1 }));
            setLoading(false);
            return;
          }
          if (!cnpMatchesBirth(u.cnp, u.birthDate)) {
            setError(t('companyForm.extraAdminCnpMismatch', { num: i + 1 }));
            setLoading(false);
            return;
          }
        }
        cleanExtraAdmins.push({
          email: u.email.trim().toLowerCase(),
          firstName: u.firstName.trim(),
          lastName: u.lastName.trim(),
          phone: u.phone?.trim() || null,
          birthDate: u.birthDate || null,
          birthPlace: u.birthPlace?.trim() || null,
          homeAddress: u.homeAddress?.trim() || null,
          badgeNumber: u.badgeNumber?.trim() || null,
          bloodGroup: u.bloodGroup?.trim() || null,
          qualification: u.qualification?.trim() || null,
          specialAuthorizations: u.specialAuthorizations?.trim() || null,
          cnp: u.cnp ? u.cnp.replace(/\D/g, '') : null,
          hireDate: u.hireDate || null,
          isSSMInstructor: !!u.isSSMInstructor,
          isCadruTehnicPSI: !!u.isCadruTehnicPSI,
          hasEPI: !!u.hasEPI,
          canManageEmployees: !!u.canManageEmployees,
          isCLM: !!u.isCLM,
          role: adminRole,
        });
      }

      const response = await api.post('/companies', {
        ...companyData,
        companyType,
        parentCompanyId: companyType === 'CLIENT' ? parentCompanyId || null : null,
        offersSSM,
        offersSU,
        offersITM,
        pageGuideEnabled,
        showOnboardingWizard,
        emailsEnabled,
        ...(isSuperAdmin ? { isDemo } : {}),
        adminEmail: adminData.email,
        adminFirstName: adminData.firstName,
        adminLastName: adminData.lastName,
        adminPhone: adminData.phone || null,
        adminHomeAddress: adminData.homeAddress,
        adminBirthDate: adminData.birthDate,
        adminBirthPlace: adminData.birthPlace,
        adminBadgeNumber: adminData.badgeNumber || null,
        adminBloodGroup: adminData.bloodGroup || null,
        adminQualification: adminData.qualification || null,
        adminSpecialAuthorizations: adminData.specialAuthorizations || null,
        adminCnp: adminData.cnp ? adminData.cnp.replace(/\D/g, '') : null,
        adminHireDate: adminData.hireDate || null,
        // Flag-urile se trimit doar dacă au sens (SSM intern + serviciul activ)
        adminIsSSMInstructor: adminCanBeInternalSSM && offersSSM ? adminIsSSMInstructor : false,
        adminIsCadruTehnicPSI: adminCanBeInternalSSM && offersSU ? adminIsCadruTehnicPSI : false,
        // EPI are sens doar pentru firme care oferă SU (PSI concept). HR/CLM oricând.
        adminHasEPI: offersSU ? adminHasEPI : false,
        adminCanManageEmployees: adminCanManageEmployees,
        adminIsCLM: adminIsCLM,
        extraUsers: cleanExtraAdmins,
      });

      setCreatedInfo(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('companyForm.createError'));
    } finally {
      setLoading(false);
    }
  };

  // SVG Icons
  const CheckCircleIcon = (
    <svg
      className="w-8 h-8 text-accent-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );

  const KeyIcon = (
    <svg
      className="w-5 h-5 text-primary-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
      />
    </svg>
  );

  const BuildingIcon = (
    <svg
      className="w-5 h-5 text-primary-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );

  const ArrowLeftIcon = (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );

  const ArrowRightIcon = (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
    </svg>
  );

  const CheckIcon = (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );

  // Stepper component
  const Stepper = () => (
    <div className="flex items-center mb-8">
      {[
        { n: 1, label: t('companyForm.stepCompanyData') },
        { n: 2, label: t('companyForm.stepAdmin') },
      ].map((s, i) => (
        <div key={s.n} className="flex items-center">
          <div
            className={`flex items-center gap-2 ${
              step === s.n
                ? 'text-primary-600 dark:text-primary-300'
                : step > s.n
                  ? 'text-accent-600 dark:text-accent-300'
                  : 'text-slate-400 dark:text-slate-400'
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                step === s.n
                  ? 'bg-primary-600'
                  : step > s.n
                    ? 'bg-accent-600'
                    : 'bg-slate-300 dark:bg-slate-700'
              }`}
            >
              {step > s.n ? (
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                s.n
              )}
            </div>
            <span className="font-medium text-sm hidden sm:block">{s.label}</span>
          </div>
          {i < 1 && <div className="flex-1 h-0.5 bg-slate-200 dark:bg-slate-800 mx-3 w-8"></div>}
        </div>
      ))}
    </div>
  );

  // Success screen
  if (createdInfo) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="text-center">
          <div className="w-16 h-16 bg-accent-100 dark:bg-accent-950/50 rounded-full flex items-center justify-center mx-auto mb-4">
            {CheckCircleIcon}
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
            {t('toasts.saved')}
          </h2>
          <p className="text-slate-600 dark:text-slate-300 mb-6">
            {createdInfo.company.name} -- CUI: {createdInfo.company.cui}
          </p>

          <Alert type="info" className="text-left mb-6">
            <div>
              <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                {KeyIcon}
                {t('companyForm.credentialsAdmin')}
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-300">{t('common.email')}:</span>
                  <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">
                    {createdInfo.admin.email}
                  </span>
                </div>
                {createdInfo.admin.temporaryPassword ? (
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-300">
                      {t('companyForm.tempPasswordLabel')}
                    </span>
                    <span className="font-mono font-semibold text-warning-700 dark:text-warning-300">
                      {createdInfo.admin.temporaryPassword}
                    </span>
                  </div>
                ) : (
                  <p className="text-sm text-accent-600 dark:text-accent-400">
                    {t('companyForm.passwordSentEmail')}
                  </p>
                )}
              </div>
              <p className="text-xs mt-3 opacity-75">{t('companyForm.mustChangePasswordFirst')}</p>
            </div>
          </Alert>

          <Alert type="info" className="text-left mb-6">
            <p className="text-sm">{t('companyForm.canAddDepartmentsNow')}</p>
          </Alert>

          <div className="flex gap-3">
            <Button
              variant="primary"
              size="lg"
              className="flex-1"
              onClick={() => navigate(`/companies/${createdInfo.company.id}`)}
            >
              {t('companyForm.goToCompanyPage')}
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => navigate(isSuperAdmin ? '/ssm-companies' : '/companies')}
            >
              <span className="flex items-center justify-center gap-2">
                {ArrowLeftIcon}
                {t('common.back')}
              </span>
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate(isSuperAdmin ? '/ssm-companies' : '/companies')}
      >
        <span className="flex items-center gap-2">
          {ArrowLeftIcon}
          {t('common.back')}
        </span>
      </Button>

      {isSuperAdmin ? (
        <>
          <Card className="mb-6">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
              {t('companyForm.whatCompanyType')}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              {t('companyForm.whatCompanyTypeDesc')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                {
                  value: 'SSM',
                  label: t('companyForm.typeSSMLabel'),
                  desc: t('companyForm.typeSSMDesc'),
                  icon: '🏢',
                },
                {
                  value: 'CLIENT',
                  label: t('companyForm.typeClientLabel'),
                  desc: t('companyForm.typeClientDesc'),
                  icon: '🏭',
                },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setCompanyType(opt.value);
                    setParentCompanyId('');
                  }}
                  className={`p-4 rounded-xl border-2 text-left transition ${companyType === opt.value ? 'border-primary-600 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50' : 'border-slate-200 dark:border-slate-800 hover:border-primary-300'}`}
                >
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <span className="text-lg">{opt.icon}</span>
                    {opt.label}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
                    {opt.desc}
                  </div>
                </button>
              ))}
            </div>

            {/* Pentru CLIENT, alegem cine se ocupă de SSM */}
            {companyType === 'CLIENT' && (
              <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-800">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
                  {t('companyForm.whoHandlesSSM')}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  {t('companyForm.whoHandlesSSMDesc')}
                </p>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setParentCompanyId('')}
                    className={`w-full p-3 rounded-lg border-2 text-left transition flex items-start gap-3 ${
                      !parentCompanyId
                        ? 'border-primary-600 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50'
                        : 'border-slate-200 dark:border-slate-800 hover:border-primary-300'
                    }`}
                  >
                    <span className="text-base mt-0.5">🏠</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {t('companyForm.internalSSM')}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('companyForm.internalSSMDesc')}
                      </p>
                    </div>
                  </button>
                  {ssmCompanies.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        // Dacă nu e selectată nicio firmă, selectează prima ca să forțeze atenția pe selector
                        if (!parentCompanyId) setParentCompanyId(ssmCompanies[0].id);
                      }}
                      className={`w-full p-3 rounded-lg border-2 text-left transition flex items-start gap-3 ${
                        parentCompanyId
                          ? 'border-primary-600 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50'
                          : 'border-slate-200 dark:border-slate-800 hover:border-primary-300'
                      }`}
                    >
                      <span className="text-base mt-0.5">👥</span>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {t('companyForm.externalSSM')}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                          {t('companyForm.externalSSMDesc')}
                        </p>
                        {parentCompanyId && (
                          <select
                            value={parentCompanyId}
                            onChange={(e) => setParentCompanyId(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="select w-full text-sm"
                          >
                            {ssmCompanies.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </button>
                  )}
                  {ssmCompanies.length === 0 && (
                    <p className="text-xs text-slate-400 dark:text-slate-400 italic px-1">
                      {t('companyForm.noSSMCompanies')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </Card>

          {/* Firmă demo (SUPER_ADMIN only) — sus, înainte de câmpurile pe care le relaxează
            (CUI aici jos, CNP-urile din pașii următori), ca decizia să preceadă validările. */}
          <Card className="mb-6">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
              {t('companyForm.demoTitle')}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              {t('companyForm.demoDesc')}
            </p>
            <button
              type="button"
              onClick={() => setIsDemo(!isDemo)}
              className={`w-full p-4 rounded-xl border-2 text-left transition flex items-start gap-3 ${
                isDemo
                  ? 'border-orange-500 dark:border-orange-600 bg-orange-50 dark:bg-orange-950/40'
                  : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 hover:border-slate-300'
              }`}
            >
              <input
                type="checkbox"
                checked={isDemo}
                onChange={() => {}}
                className={`h-4 w-4 mt-0.5 rounded pointer-events-none ${isDemo ? 'text-orange-600' : 'text-slate-300 dark:text-slate-600'}`}
              />
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {t('companyForm.demoToggleLabel')}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {t('companyForm.demoToggleSublabel')}
                </p>
              </div>
            </button>
          </Card>
        </>
      ) : (
        <Alert type="info" className="mb-6">
          <div>
            <p className="text-sm font-semibold flex items-center gap-2">
              {BuildingIcon}
              {t('companyForm.creatingClientCompany')}
            </p>
            <p className="text-xs mt-1 opacity-75">{t('companyForm.creatingClientCompanyDesc')}</p>
          </div>
        </Alert>
      )}

      <Stepper />

      {error && (
        <Alert type="error" className="mb-6" onDismiss={() => setError('')}>
          <p className="font-medium">{error}</p>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* STEP 1 -- Date firma */}
        {step === 1 && (
          <Card>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-5">
              {companyType === 'SSM'
                ? t('companies.ssmCompaniesTitle')
                : t('companies.clientCompaniesTitle')}
            </h1>

            <div className="space-y-5">
              {/* CUI sus de tot: după preluarea de la ANAF se precompletează câmpurile de mai jos. */}
              <div>
                <CuiInput
                  label={t('companies.cui')}
                  required
                  value={companyData.cui}
                  onChange={(v) => {
                    setCompanyData((prev) => ({ ...prev, cui: v }));
                    setError('');
                    setAnafNote(null);
                  }}
                  onBlur={() => markTouched('company.cui')}
                  error={liveErrors['company.cui']}
                  helper={t('companyForm.cuiHelper')}
                />
                <div className="mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAnafLookup}
                    loading={anafLoading}
                    disabled={anafLoading}
                  >
                    {anafLoading ? t('companyForm.anafLoadingBtn') : t('companyForm.anafFetchBtn')}
                  </Button>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {t('companyForm.anafFetchHint')}
                  </p>
                  {anafNote && (
                    <p
                      className={`text-xs mt-2 font-medium ${
                        anafNote.type === 'error'
                          ? 'text-danger-600 dark:text-danger-300'
                          : anafNote.type === 'warning'
                            ? 'text-warning-700 dark:text-warning-300'
                            : 'text-accent-600 dark:text-accent-300'
                      }`}
                    >
                      {anafNote.msg}
                    </p>
                  )}
                </div>
              </div>

              <Input
                label={t('common.name') + ' *'}
                name="name"
                required
                value={companyData.name}
                onChange={handleCompanyChange}
              />

              <Input
                label={t('common.address')}
                name="address"
                value={companyData.address}
                onChange={handleCompanyChange}
              />

              <PhoneInput
                label={t('common.phone')}
                value={companyData.phone}
                onChange={(v) => {
                  setCompanyData((prev) => ({ ...prev, phone: v }));
                  setError('');
                }}
                onBlur={() => markTouched('company.phone')}
                error={liveErrors['company.phone']}
              />
              <Input
                label={t('common.email')}
                type="email"
                name="email"
                value={companyData.email}
                onChange={handleCompanyChange}
                onBlur={() => markTouched('company.email')}
                error={liveErrors['company.email']}
              />

              {/* Servicii oferite — selector clar cu 2 carduri bifabile */}
              <div className="pt-2">
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
                  {t('companyForm.servicesActive')}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                  {t('companyForm.servicesActiveDesc')}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setOffersSSM(!offersSSM)}
                    className={`p-4 rounded-xl border-2 text-left transition flex items-start gap-3 ${
                      offersSSM
                        ? 'border-primary-600 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50'
                        : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 hover:border-primary-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={offersSSM}
                      onChange={() => {}}
                      className="h-4 w-4 mt-0.5 text-primary-600 rounded pointer-events-none"
                    />
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {t('companyForm.serviceSSMLabel')}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {t('companyForm.serviceSSMDesc')}
                      </p>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOffersSU(!offersSU)}
                    className={`p-4 rounded-xl border-2 text-left transition flex items-start gap-3 ${
                      offersSU
                        ? 'border-warning-600 dark:border-warning-700 bg-warning-50 dark:bg-warning-950/50'
                        : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 hover:border-warning-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={offersSU}
                      onChange={() => {}}
                      className="h-4 w-4 mt-0.5 text-warning-600 rounded pointer-events-none"
                    />
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {t('companyForm.serviceSULabel')}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {t('companyForm.serviceSUDesc')}
                      </p>
                    </div>
                  </button>
                </div>
                {!offersSSM && !offersSU && (
                  <p className="text-xs text-danger-600 dark:text-danger-300 mt-2">
                    {t('companyForm.atLeastOneServiceWarning')}
                  </p>
                )}

                {/* Al 3-lea card — Raport ITM (add-on plătit separat). Vizual distinct (violet +
                    badge „add-on"), default nebifat. Nu intră în regula „cel puțin un serviciu".
                    DOAR pentru firmele CLIENT — o firmă SSM care prestează servicii externe nu are
                    nevoie de raport ITM pentru ea însăși (owner). */}
                {companyType === 'CLIENT' && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => setOffersITM(!offersITM)}
                      className={`w-full p-4 rounded-xl border-2 text-left transition flex items-start gap-3 ${
                        offersITM
                          ? 'border-violet-500 dark:border-violet-600 bg-violet-50 dark:bg-violet-950/40'
                          : 'border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-violet-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={offersITM}
                        onChange={() => {}}
                        className="h-4 w-4 mt-0.5 text-violet-600 rounded pointer-events-none"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {t('itmService.cardTitle')}
                          </p>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-violet-100 dark:bg-violet-900/60 text-violet-700 dark:text-violet-200">
                            {t('itmService.addonBadge')}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {t('itmService.cardSublabel')}
                        </p>
                      </div>
                    </button>
                  </div>
                )}

                {/* #21: ghid de pagină (tururi contextuale) — activ implicit; poate fi oprit per firmă. */}
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setPageGuideEnabled(!pageGuideEnabled)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition flex items-start gap-3 ${
                      pageGuideEnabled
                        ? 'border-primary-500 dark:border-primary-600 bg-primary-50 dark:bg-primary-950/40'
                        : 'border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-primary-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={pageGuideEnabled}
                      onChange={() => {}}
                      className="h-4 w-4 mt-0.5 text-primary-600 rounded pointer-events-none"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {t('itmService.pageGuideTitle')}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {t('itmService.pageGuideDesc')}
                      </p>
                    </div>
                  </button>
                </div>

                {/* Wizard de onboarding — OPT-IN per firmă. SSM-istul bifează pentru clienții
                    care vor un tur ghidat al platformei la prima logare. Default off. */}
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setShowOnboardingWizard(!showOnboardingWizard)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition flex items-start gap-3 ${
                      showOnboardingWizard
                        ? 'border-primary-500 dark:border-primary-600 bg-primary-50 dark:bg-primary-950/40'
                        : 'border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-primary-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={showOnboardingWizard}
                      onChange={() => {}}
                      className="h-4 w-4 mt-0.5 text-primary-600 rounded pointer-events-none"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {t('itmService.onboardingWizardTitle')}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {t('itmService.onboardingWizardDesc')}
                      </p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Email-uri automate — aceeași formă ca toggle-ul din tab-ul Info al firmei
                  (CompanyDetails.js). Oricine poate crea firme (SUPER_ADMIN sau SSM) poate
                  decide de la creare dacă firma primește email-uri automate. */}
              <Card>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1">
                  {t('emailToggle.title')}
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                  {t('emailToggle.description')}
                </p>
                <button
                  type="button"
                  onClick={() => setEmailsEnabled(!emailsEnabled)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition flex items-start gap-3 ${
                    emailsEnabled
                      ? 'border-blue-600 bg-blue-50 dark:bg-blue-950/50'
                      : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-800 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={emailsEnabled}
                    onChange={() => {}}
                    className={`h-4 w-4 mt-0.5 rounded pointer-events-none ${emailsEnabled ? 'text-blue-600' : 'text-slate-300 dark:text-slate-600'}`}
                  />
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {t('emailToggle.label')}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t('emailToggle.sublabel')}
                    </p>
                  </div>
                </button>
              </Card>

              <Button
                type="button"
                variant="primary"
                size="lg"
                className="w-full"
                onClick={async () => {
                  const isValid = await validateStep1();
                  if (isValid) setStep(2);
                }}
              >
                <span className="flex items-center justify-center gap-2">
                  {t('myCourses.continueTraining')}
                  {ArrowRightIcon}
                </span>
              </Button>
            </div>
          </Card>
        )}

        {/* STEP 2 -- Administrator */}
        {step === 2 && (
          <Card>
            <div className="mb-5">
              <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">
                {t('companyDetails.admissionTitle')}
              </h1>
            </div>

            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t('employees.firstName') + ' *'}
                  name="firstName"
                  required
                  value={adminData.firstName}
                  onChange={handleAdminChange}
                  onBlur={() => markTouched('admin.firstName')}
                  error={liveErrors['admin.firstName']}
                />
                <Input
                  label={t('employees.lastName') + ' *'}
                  name="lastName"
                  required
                  value={adminData.lastName}
                  onChange={handleAdminChange}
                  onBlur={() => markTouched('admin.lastName')}
                  error={liveErrors['admin.lastName']}
                />
              </div>

              <Input
                label={t('common.email') + ' *'}
                type="email"
                name="email"
                required
                value={adminData.email}
                onChange={handleAdminChange}
                onBlur={() => markTouched('admin.email')}
                error={liveErrors['admin.email']}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="input-label">{t('employees.birthDate')} *</label>
                  <DateSelect
                    value={adminData.birthDate}
                    onChange={(v) => {
                      setAdminData((prev) => ({ ...prev, birthDate: v }));
                      markTouched('admin.birthDate');
                    }}
                    yearStart={1940}
                    error={liveErrors['admin.birthDate']}
                    birthDate
                  />
                </div>
                <Input
                  label={t('employees.birthPlace') + ' *'}
                  name="birthPlace"
                  required
                  value={adminData.birthPlace}
                  onChange={handleAdminChange}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <PhoneInput
                  label={t('common.phone')}
                  value={adminData.phone}
                  onChange={(v) => {
                    setAdminData((prev) => ({ ...prev, phone: v }));
                    setError('');
                  }}
                  onBlur={() => markTouched('admin.phone')}
                  error={liveErrors['admin.phone']}
                />
                <div>
                  <label className="input-label">{t('employees.hireDate')} *</label>
                  <DateSelect
                    value={adminData.hireDate}
                    onChange={(v) => setAdminData((prev) => ({ ...prev, hireDate: v }))}
                    yearStart={1980}
                    maxDate={null}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t('employees.badge')}
                  name="badgeNumber"
                  value={adminData.badgeNumber}
                  onChange={handleAdminChange}
                />
                <Input
                  label={t('employees.bloodGroup')}
                  name="bloodGroup"
                  value={adminData.bloodGroup}
                  onChange={handleAdminChange}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t('employees.qualification')}
                  name="qualification"
                  value={adminData.qualification}
                  onChange={handleAdminChange}
                  placeholder={t('companyForm.qualificationPlaceholder')}
                />
                <Input
                  label={t('employees.specialAuths')}
                  name="specialAuthorizations"
                  value={adminData.specialAuthorizations}
                  onChange={handleAdminChange}
                />
              </div>

              {/* CNP-ul adminului — opțional. Dacă e completat, criptat la salvare și adminul sare
                  pasul CNP la primul login. Altfel îl introduce singur prin wizard. */}
              <Input
                label={t('companyForm.cnpOptionalLabel')}
                name="cnp"
                value={adminData.cnp}
                onChange={handleAdminCnpChange}
                onBlur={() => markTouched('admin.cnp')}
                error={liveErrors['admin.cnp']}
                placeholder={t('companyForm.cnpEncryptPlaceholder')}
                helper={t('companyForm.cnpAdminHelperPrefix') + t('employees.cnpAutofillNote')}
                maxLength={13}
              />

              {/* Flag-uri admin: apar DOAR dacă firma se ocupă singură de SSM/SU.
                  - SSM extern (parentCompanyId): nu apar — de SSM se ocupă firma SSM
                  - SSM (firma de tip SSM) sau CLIENT standalone: apar, condiționate pe servicii */}
              {adminCanBeInternalSSM && (offersSSM || offersSU) && (
                <div className="space-y-3">
                  <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-xs text-slate-600 dark:text-slate-300">
                    <Trans
                      i18nKey="companyForm.internalSSMHint"
                      values={{
                        admin:
                          companyType === 'SSM'
                            ? t('companyForm.adminSSMCompany')
                            : t('companyForm.adminCompany'),
                      }}
                      components={{ b: <strong /> }}
                    />
                  </div>
                  {offersSSM && (
                    <div
                      className={`p-4 rounded-xl border-2 transition ${adminIsSSMInstructor ? 'border-primary-600 dark:border-primary-700 bg-primary-50 dark:bg-primary-950/50' : 'border-slate-200 dark:border-slate-800'}`}
                    >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={adminIsSSMInstructor}
                          onChange={(e) => setAdminIsSSMInstructor(e.target.checked)}
                          className="h-4 w-4 mt-0.5 text-primary-600 rounded"
                        />
                        <div>
                          <p className="text-sm font-semibold text-primary-900 dark:text-primary-200">
                            {t('companyForm.adminIsInspectorSSM')}
                          </p>
                          <p className="text-xs text-primary-700 dark:text-primary-300 mt-0.5">
                            {t('companyForm.adminIsInspectorSSMDesc')}
                          </p>
                        </div>
                      </label>
                    </div>
                  )}
                  {offersSU && (
                    <div
                      className={`p-4 rounded-xl border-2 transition ${adminIsCadruTehnicPSI ? 'border-warning-600 dark:border-warning-700 bg-warning-50 dark:bg-warning-950/50' : 'border-slate-200 dark:border-slate-800'}`}
                    >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={adminIsCadruTehnicPSI}
                          onChange={(e) => setAdminIsCadruTehnicPSI(e.target.checked)}
                          className="h-4 w-4 mt-0.5 text-warning-600 rounded"
                        />
                        <div>
                          <p className="text-sm font-semibold text-warning-900 dark:text-warning-300">
                            {t('companyForm.adminIsCadruPSI')}
                          </p>
                          <p className="text-xs text-warning-700 dark:text-warning-300 mt-0.5">
                            {t('companyForm.adminIsCadruPSIDesc')}
                          </p>
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              )}

              {/* Pentru firmele CLIENT cu SSM extern (explicit selectat sau implicit pentru SSM_COMPANY_ADMIN),
                  explicăm că flag-urile nu sunt necesare */}
              {companyType === 'CLIENT' && (parentCompanyId || !isSuperAdmin) && (
                <div className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-xs text-slate-600 dark:text-slate-300">
                  {t('companyForm.externalSSMNote')}
                </div>
              )}

              {/* Flag-uri operaționale — paritate cu formularul de angajat (EPI / Gestionar angajați / CLM).
                  EPI apare doar dacă firma oferă SU (echipamentul protecție individuală e concept PSI).
                  La crearea firmei nu există încă departamente; CLM va fi „global" până la asignare manuală. */}
              <div className="border-t border-slate-200 dark:border-slate-800 pt-4 mt-2 space-y-2">
                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                  {t('companyForm.operationalRoles')}
                </p>
                {offersSU && (
                  <label className="flex items-start gap-3 p-3 border border-slate-200 dark:border-slate-800 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                    <input
                      type="checkbox"
                      checked={adminHasEPI}
                      onChange={(e) => setAdminHasEPI(e.target.checked)}
                      className="h-4 w-4 mt-0.5 text-danger-600 rounded"
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                        {t('employees.beneficiarEPI')}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('employees.beneficiarEPIHelp')}
                      </p>
                    </div>
                  </label>
                )}
                <label className="flex items-start gap-3 p-3 border border-slate-200 dark:border-slate-800 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                  <input
                    type="checkbox"
                    checked={adminCanManageEmployees}
                    onChange={(e) => setAdminCanManageEmployees(e.target.checked)}
                    className="h-4 w-4 mt-0.5 text-primary-600 rounded"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {t('employees.canManageEmployees')}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t('employees.canManageEmployeesHelp')}
                    </p>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 border border-slate-200 dark:border-slate-800 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                  <input
                    type="checkbox"
                    checked={adminIsCLM}
                    onChange={(e) => tryToggleAdminCLM(e.target.checked)}
                    className="h-4 w-4 mt-0.5 text-primary-600 rounded"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {t('employees.clmTitle')}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {t('employees.clmDesc')}
                      {t('companyForm.clmDescSuffix')}
                    </p>
                  </div>
                </label>
              </div>

              {/* Administratori adiționali — opționali. Câmpuri minime; rolul de admin se prefixează
                  automat în funcție de tipul firmei. Datele extra (semnătură, traseu, etc.) le vor
                  completa adminii înșiși la primul login prin wizard. */}
              <div className="border-t border-slate-200 dark:border-slate-800 pt-4 mt-2">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {t('companyForm.addMoreAdmins')}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                      {t('companyForm.addMoreAdminsDesc')}
                    </p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={addExtraAdmin}>
                    {t('companyForm.addAnother')}
                  </Button>
                </div>

                {extraUsers.length > 0 && (
                  <div className="space-y-5">
                    {extraUsers.map((u, idx) => (
                      <div
                        key={idx}
                        className="p-4 rounded-lg border-2 border-primary-200 dark:border-primary-800 bg-primary-50/30 dark:bg-primary-950/20 space-y-4"
                      >
                        <div className="flex items-center justify-between border-b border-primary-200 dark:border-primary-800 pb-2">
                          <p className="text-sm font-semibold text-primary-800 dark:text-primary-200">
                            {t('companyForm.adminNum', { num: idx + 2 })}
                          </p>
                          <button
                            type="button"
                            onClick={() => removeExtraAdmin(idx)}
                            className="text-xs text-danger-600 dark:text-danger-300 hover:text-danger-700 font-medium"
                          >
                            × {t('common.delete')}
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Input
                            label={t('employees.firstName') + ' *'}
                            value={u.firstName}
                            onChange={(e) => updateExtraAdmin(idx, 'firstName', e.target.value)}
                            onBlur={() => markTouched(`extra.${idx}.firstName`)}
                            error={liveErrors[`extra.${idx}.firstName`]}
                          />
                          <Input
                            label={t('employees.lastName') + ' *'}
                            value={u.lastName}
                            onChange={(e) => updateExtraAdmin(idx, 'lastName', e.target.value)}
                            onBlur={() => markTouched(`extra.${idx}.lastName`)}
                            error={liveErrors[`extra.${idx}.lastName`]}
                          />
                        </div>

                        <Input
                          label={t('common.email') + ' *'}
                          type="email"
                          value={u.email}
                          onChange={(e) => updateExtraAdmin(idx, 'email', e.target.value)}
                          onBlur={() => markTouched(`extra.${idx}.email`)}
                          error={liveErrors[`extra.${idx}.email`]}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="input-label">{t('employees.birthDate')} *</label>
                            <DateSelect
                              value={u.birthDate}
                              onChange={(v) => {
                                updateExtraAdmin(idx, 'birthDate', v);
                                markTouched(`extra.${idx}.birthDate`);
                              }}
                              yearStart={1940}
                              error={liveErrors[`extra.${idx}.birthDate`]}
                              birthDate
                            />
                          </div>
                          <Input
                            label={t('employees.birthPlace') + ' *'}
                            value={u.birthPlace}
                            onChange={(e) => updateExtraAdmin(idx, 'birthPlace', e.target.value)}
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <PhoneInput
                            label={t('common.phone')}
                            value={u.phone}
                            onChange={(v) => updateExtraAdmin(idx, 'phone', v)}
                            onBlur={() => markTouched(`extra.${idx}.phone`)}
                            error={liveErrors[`extra.${idx}.phone`]}
                          />
                          <div>
                            <label className="input-label">{t('employees.hireDate')} *</label>
                            <DateSelect
                              value={u.hireDate}
                              onChange={(v) => updateExtraAdmin(idx, 'hireDate', v)}
                              yearStart={1980}
                              maxDate={null}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Input
                            label={t('employees.badge')}
                            value={u.badgeNumber}
                            onChange={(e) => updateExtraAdmin(idx, 'badgeNumber', e.target.value)}
                          />
                          <Input
                            label={t('employees.bloodGroup')}
                            value={u.bloodGroup}
                            onChange={(e) => updateExtraAdmin(idx, 'bloodGroup', e.target.value)}
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Input
                            label={t('employees.qualification')}
                            value={u.qualification}
                            onChange={(e) => updateExtraAdmin(idx, 'qualification', e.target.value)}
                          />
                          <Input
                            label={t('employees.specialAuths')}
                            value={u.specialAuthorizations}
                            onChange={(e) =>
                              updateExtraAdmin(idx, 'specialAuthorizations', e.target.value)
                            }
                          />
                        </div>

                        <Input
                          label={t('companyForm.cnpOptionalLabel')}
                          value={u.cnp}
                          onChange={(e) => updateExtraAdminCnp(idx, e.target.value)}
                          onBlur={() => markTouched(`extra.${idx}.cnp`)}
                          error={liveErrors[`extra.${idx}.cnp`]}
                          placeholder={t('companyForm.cnpEncryptPlaceholder')}
                          helper={t('employees.cnpAutofillNote')}
                          maxLength={13}
                        />

                        {/* Flag-uri SSM/PSI — apar doar dacă firma își face SSM-ul singură (la fel ca pentru primul admin). */}
                        {adminCanBeInternalSSM && (offersSSM || offersSU) && (
                          <div className="space-y-2 pt-2 border-t border-primary-200 dark:border-primary-800">
                            {offersSSM && (
                              <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-white/40 dark:hover:bg-slate-800/40">
                                <input
                                  type="checkbox"
                                  checked={u.isSSMInstructor}
                                  onChange={(e) =>
                                    updateExtraAdmin(idx, 'isSSMInstructor', e.target.checked)
                                  }
                                  className="h-4 w-4 mt-0.5 text-primary-600 rounded"
                                />
                                <div>
                                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                    {t('companyForm.alsoInspectorSSM')}
                                  </p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {t('companyForm.canSignSSMSheets')}
                                  </p>
                                </div>
                              </label>
                            )}
                            {offersSU && (
                              <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-white/40 dark:hover:bg-slate-800/40">
                                <input
                                  type="checkbox"
                                  checked={u.isCadruTehnicPSI}
                                  onChange={(e) =>
                                    updateExtraAdmin(idx, 'isCadruTehnicPSI', e.target.checked)
                                  }
                                  className="h-4 w-4 mt-0.5 text-warning-600 rounded"
                                />
                                <div>
                                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                    {t('companyForm.alsoCadruPSI')}
                                  </p>
                                  <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {t('companyForm.canSignSUSheets')}
                                  </p>
                                </div>
                              </label>
                            )}
                          </div>
                        )}

                        {/* Flag-uri operaționale — paritate cu primul admin: EPI (doar dacă firma oferă SU),
                            Gestionar angajați (HR) și CLM (asignare departamente după creare). */}
                        <div className="space-y-2 pt-2 border-t border-primary-200 dark:border-primary-800">
                          {offersSU && (
                            <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-white/40 dark:hover:bg-slate-800/40">
                              <input
                                type="checkbox"
                                checked={u.hasEPI}
                                onChange={(e) => updateExtraAdmin(idx, 'hasEPI', e.target.checked)}
                                className="h-4 w-4 mt-0.5 text-danger-600 rounded"
                              />
                              <div>
                                <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                  {t('employees.beneficiarEPI')}
                                </p>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                  {t('employees.beneficiarEPIHelp')}
                                </p>
                              </div>
                            </label>
                          )}
                          <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-white/40 dark:hover:bg-slate-800/40">
                            <input
                              type="checkbox"
                              checked={u.canManageEmployees}
                              onChange={(e) =>
                                updateExtraAdmin(idx, 'canManageEmployees', e.target.checked)
                              }
                              className="h-4 w-4 mt-0.5 text-primary-600 rounded"
                            />
                            <div>
                              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                {t('employees.canManageEmployees')}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {t('employees.canManageEmployeesHelp')}
                              </p>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-white/40 dark:hover:bg-slate-800/40">
                            <input
                              type="checkbox"
                              checked={u.isCLM}
                              onChange={(e) => tryToggleExtraCLM(idx, e.target.checked)}
                              className="h-4 w-4 mt-0.5 text-primary-600 rounded"
                            />
                            <div>
                              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                                {t('employees.clmTitle')}
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                {t('employees.clmDesc')}
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="secondary" onClick={() => setStep(1)}>
                  <span className="flex items-center gap-2">
                    {ArrowLeftIcon}
                    {t('common.back')}
                  </span>
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="flex-1"
                  loading={loading}
                  disabled={loading}
                >
                  <span className="flex items-center justify-center gap-2">
                    {CheckIcon}
                    {loading ? t('common.loading') : t('common.save')}
                  </span>
                </Button>
              </div>
            </div>
          </Card>
        )}
      </form>
    </div>
  );
};

export default CompanyForm;
